import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const AUTH_TOKEN_KEYS = ['auth_token', 'auth_v2_accessToken', 'accessToken', 'authToken', 'token'];
const AUTH_USER_KEYS = ['auth_user', 'auth_v2_user', 'currentUser', 'user'];

type AuthUser = {
  id?: string;
  _id?: string;
  email?: string;
  name?: string;
  role?: string;
  systemRole?: string;
  tenantName?: string;
  firmName?: string;
  is_internal_admin?: boolean;
};

type AuthContextValue = {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (token: string, user?: AuthUser | null) => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const readAccessToken = (): string => {
  if (typeof window === 'undefined') return '';
  for (const key of AUTH_TOKEN_KEYS) {
    const value = String(window.localStorage.getItem(key) || '').trim();
    if (value && value.split('.').length >= 3) return value;
  }
  return '';
};

const decodeJwtPayload = (token: string): Record<string, any> | null => {
  try {
    const parts = String(token || '').split('.');
    if (parts.length < 2) return null;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch (_) {
    return null;
  }
};

const isTokenExpired = (token: string): boolean => {
  const payload = decodeJwtPayload(token);
  const exp = Number(payload?.exp || 0);
  return exp > 0 && Date.now() >= exp * 1000;
};

const getCurrentHost = (): string => {
  if (typeof window === 'undefined') return 'admin.bassitai.com';
  return String(window.location.host || '').split(':')[0].toLowerCase();
};

const isLocalHost = (host: string): boolean =>
  host === 'localhost' || host === '127.0.0.1' || host.endsWith('.localhost');

const getBaseDomain = (host: string): string => {
  if (!host) return 'bassitai.com';
  if (isLocalHost(host)) return 'localhost';
  const parts = host.split('.').filter(Boolean);
  if (parts.length < 2) return host;
  return parts.slice(-2).join('.');
};

const buildLoginUrl = (): string => {
  if (typeof window === 'undefined') return 'https://login.bassitai.com/login';
  const host = getCurrentHost();
  const baseDomain = getBaseDomain(host);
  const protocol = isLocalHost(host) ? 'http' : 'https';
  const loginHost = baseDomain === 'localhost' ? 'login.localhost' : `login.${baseDomain}`;
  const returnTo = String(window.location.host || host);
  return `${protocol}://${loginHost}/login?returnTo=${encodeURIComponent(returnTo)}`;
};

const readStoredUser = (): AuthUser | null => {
  if (typeof window === 'undefined') return null;
  for (const key of AUTH_USER_KEYS) {
    const raw = String(window.localStorage.getItem(key) || '').trim();
    if (!raw) continue;
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') return parsed as AuthUser;
    } catch (_) {}
  }
  return null;
};

const deriveUserFromToken = (token: string): AuthUser | null => {
  const payload = decodeJwtPayload(token);
  if (!payload) return null;
  return {
    id: String(payload.sub || payload.userId || payload.id || ''),
    _id: String(payload.sub || payload.userId || payload.id || ''),
    email: String(payload.email || ''),
    name: String(payload.name || payload.displayName || payload.email || 'Admin'),
    role: String(payload.role || '').toLowerCase(),
    systemRole: String(payload.systemRole || payload.role || '').toLowerCase(),
    is_internal_admin: Boolean(payload.is_internal_admin || payload.isInternalAdmin),
  };
};

const readAuthState = (): { token: string; user: AuthUser | null } => {
  const token = readAccessToken();
  if (!token || isTokenExpired(token)) {
    return { token: '', user: null };
  }
  const tokenUser = deriveUserFromToken(token);
  const storedUser = readStoredUser();
  return {
    token,
    user: {
      ...(storedUser || {}),
      ...(tokenUser || {}),
    },
  };
};

export const AuthProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState('');
  const [user, setUser] = useState<AuthUser | null>(null);

  useEffect(() => {
    const syncAuthState = () => {
      const state = readAuthState();
      setToken(state.token);
      setUser(state.user);
      setIsLoading(false);
    };
    syncAuthState();
    const onStorage = () => syncAuthState();
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const login = async (nextToken: string, nextUser?: AuthUser | null): Promise<void> => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('auth_token', nextToken);
    if (nextUser) {
      window.localStorage.setItem('auth_user', JSON.stringify(nextUser));
    }
    const state = readAuthState();
    setToken(state.token);
    setUser(state.user);
  };

  const logout = async (): Promise<void> => {
    if (typeof window === 'undefined') return;
    AUTH_TOKEN_KEYS.forEach((key) => window.localStorage.removeItem(key));
    AUTH_USER_KEYS.forEach((key) => window.localStorage.removeItem(key));
    setToken('');
    setUser(null);
    window.location.replace(buildLoginUrl());
  };

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: Boolean(token),
      isLoading,
      login,
      logout,
    }),
    [user, token, isLoading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
};

